import { noop } from '@portal/components'
import { createContext, useContext } from 'react'

import { DQLEditorDataAccessEvent, DQLHTTPResponse } from '../../types'
import { Column } from '../queryResultTable/QueryResultTable'

export type DQLEditorPortalContext = {
  /**
   * The ID of the app
   */
  appId?: string
  /**
   * The name of the app
   */
  appName?: string
  /**
   * The URL to the app's settings page
   */
  appSettingsUrl?: string
  /**
   * The collections that the app has. This will be a livequery result passed from
   * the portal for proper reactivity
   */
  appCollections?: string[]
  /**
   * Whether the app collections are still loading
   */
  isLoadingCollections?: boolean
  /**
   * The URL to return to when the user clicks the "X" button
   */
  returnToUrl: string
  /**
   * The tailwind config to use for the portal
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tailwind?: any
  /**
   * A function to log an access event to the audit log
   */
  logDataAccessedEvent?: (event: DQLEditorDataAccessEvent) => void
}
export type DQLEditorLayoutProps = {
  /**
   * Whether or not the sidebar is open
   */
  sideBarOpen: boolean
  /**
   * A setter for the sidebar open state
   */
  setSideBarOpen: (open: boolean) => void
  /**
   * Whether the history sidebar is open
   */
  historySideBarOpen: boolean
  /**
   * A setter for the history sidebar open state
   */
  setHistorySideBarOpen: (open: boolean) => void
  /**
   * The active view of the editor
   */
  activeView: 'collections' | 'editor'
  /**
   * A setter for the active view
   */
  setActiveView: (view: 'collections' | 'editor') => void
  /**
   * The active, selected row to display data for
   */
  activeRow?: Column
  /**
   * A setter for the active row
   */
  setActiveRow: (row: Column | undefined) => void
  /**
   * Whether or not to show the latest tour
   */
  showLatestTour: boolean
  /**
   * A setter for the latest tour
   */
  setShowLatestTour: (show: boolean) => void
}
export type IDQLEditorContext = {
  /**
   * The DQL query to run
   */
  query?: string
  /**
   * A setter for the DQL query
   * @param query The query to set
   * @param execute Optional flag to determine if the query should be executed. Defaults to true.
   */
  setQuery: (query: string, execute?: boolean) => void
  /**
   * A function to refetch the current DQL query
   */
  refetch: () => void
  /**
   * The refetch interval in milliseconds for automatic polling
   */
  refetchInterval: number | false
  /**
   * A setter for the refetch interval
   */
  setRefetchInterval: (interval: number | false) => void
  /**
   * The results of the DQL query. This is the response from the HTTP API
   */
  result?: DQLHTTPResponse
  /**
   * Whether or not the query is currently loading
   */
  isLoading: boolean
  /**
   * The access token to use when interacting with the HTTP API. This is not directly
   * used by the DQLEditor, it is used to determine ready state for issuing queries.
   */
  accessToken?: string
  /**
   * An error message to display to the user, if any, from the DQL query response
   */
  errorMessage?: string
} & DQLEditorPortalContext &
  DQLEditorLayoutProps
export const DQLEditorContext = createContext<IDQLEditorContext>({
  isLoading: false,
  errorMessage: undefined,
  query: undefined,
  setQuery: noop,
  refetch: noop,
  refetchInterval: 0,
  setRefetchInterval: noop,

  appName: undefined,
  appSettingsUrl: undefined,
  returnToUrl: '',

  sideBarOpen: false,
  setSideBarOpen: noop,

  historySideBarOpen: true,
  setHistorySideBarOpen: noop,

  activeView: 'collections',
  setActiveView: noop,

  activeRow: undefined,
  setActiveRow: noop,

  showLatestTour: false,
  setShowLatestTour: noop,
})

export const useDQLEditorContext = () => useContext(DQLEditorContext)
